import { ScannerModel } from "@/types/models";
import { getToken } from "./auth.service";
import { privateApi } from "./api";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const getScanner = async ({ projectId, scannerId }: { projectId: string; scannerId: string }) => {
    return await privateApi.get<ScannerModel>(`/project/${projectId}/scanner/${scannerId}`);
};

export const useScanner = (projectId?: string, scannerId?: string) => {
    return useQuery({
        queryKey: ['scanner', projectId, scannerId],
        queryFn: () => {
            if (!projectId || !scannerId) return Promise.reject("Project ID or Scanner ID missing");
            return getScanner({ projectId: projectId!, scannerId: scannerId! }).then((response) => response.data);
        },
        enabled: !!projectId && !!scannerId,
    });
};

// ---- Backoffice -------------------------------------------------------------

/** A row of the Scanner table, with the project and sample it is bound to. */
export type BackOfficeScanner = {
    serialNumber: string;
    projectId: string;
    projectName: string | null;
    sampleId: string | null;
    sampleName: string | null;
    hubId: string | null;
    // Sent but not shown for now: the acquisition window (t, x, y, l), the
    // mode and the quality are not used yet.
    mode: string;
    t: number;
    x: number;
    y: number;
    l: number;
    resolutionDpi: number;
    quality: number;
    acquisitionCount: number;
    cropTopMm: number;
    cropBottomMm: number;
    cropLeftMm: number;
    cropRightMm: number;
    topEdgeCm: number;
    createdAt: string;
    updatedAt: string;
};

export type ScannerCropDetails = {
    serialNumber: string;
    projectId: string;
    projectName: string | null;
    sample: { id: string; name: string; projectId: string } | null;
    // The sample's first acquisition, the one the frame is drawn on.
    image: { id: string; acquisitionDate: string; dpi: number | null } | null;
    // IIIF info.json, or null while the jp2 has not landed in S3.
    imageUrl: string | null;
    cropTopMm: number;
    cropBottomMm: number;
    cropLeftMm: number;
    cropRightMm: number;
};

export type ScannerCropValues = Pick<
    ScannerCropDetails,
    'cropTopMm' | 'cropBottomMm' | 'cropLeftMm' | 'cropRightMm'
>;

const getBackOfficeScanners = async () => {
    const { data } = await privateApi.get<BackOfficeScanner[]>('/scanner');
    return data;
};

const getScannerCrop = async (serialNumber: string) => {
    const { data } = await privateApi.get<ScannerCropDetails>(
        `/scanner/${encodeURIComponent(serialNumber)}/crop`,
    );
    return data;
};

const setScannerCrop = async ({
    serialNumber,
    crop,
}: {
    serialNumber: string;
    crop: ScannerCropValues;
}) => {
    await privateApi.patch(
        `/scanner/${encodeURIComponent(serialNumber)}/crop`,
        crop,
    );
};

export const useBackOfficeScanners = () => {
    return useQuery({
        queryKey: ['backOfficeScanners'],
        queryFn: getBackOfficeScanners,
        enabled: !!getToken(),
    });
};

export const useScannerCrop = (serialNumber?: string) => {
    return useQuery({
        queryKey: ['scannerCrop', serialNumber],
        queryFn: () => getScannerCrop(serialNumber!),
        enabled: !!serialNumber && !!getToken(),
        refetchOnWindowFocus: false,
    });
};

export const useSetScannerTopEdge = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            serialNumber,
            topEdgeCm,
        }: {
            serialNumber: string;
            topEdgeCm: number;
        }) => {
            await privateApi.patch(
                `/scanner/${encodeURIComponent(serialNumber)}/top-edge`,
                { topEdgeCm },
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['backOfficeScanners'] });
            // The viewer's depth scale reads it off the scanner.
            queryClient.invalidateQueries({ queryKey: ['scanner'] });
        },
    });
};

export const useSetScannerCrop = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: setScannerCrop,
        onSuccess: (_, { serialNumber }) => {
            queryClient.invalidateQueries({ queryKey: ['backOfficeScanners'] });
            queryClient.invalidateQueries({ queryKey: ['scannerCrop', serialNumber] });
            queryClient.invalidateQueries({ queryKey: ['scanner'] });
        },
        onError: (error) => {
            console.error('Error while updating scanner crop', error);
        },
    });
};
